'use client';

import { useAuthStore } from '@/hooks/use-auth';
import { projectLocalTimeRange } from '@/lib/timezone-utils';

/**
 * Project a UTC-stored weekly slot into the viewer's timezone for display.
 *
 * Class sections are stored as UTC (see timezone-utils.ts header + the
 * quick-spec for migration 0080). Every display surface that renders a
 * stored `dayOfWeek` / `startTime` / `endTime` triple to a viewer must
 * convert through this hook so a 9 AM ET class shows as 6 AM PT for a
 * Pacific-time parent and 9 PM BJT for a Beijing parent.
 *
 * For per-row conversions inside a `.map()` (e.g. `<option>` labels in a
 * `<select>`), call `useViewerTimezone()` once at the component level and
 * pass it to `projectLocalTimeRange()` per row to avoid rules-of-hooks
 * violations.
 */
export function useLocalTimeRange(
  dayOfWeek: number,
  startTimeUtc: string,
  endTimeUtc: string,
  refDate?: Date,
): {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  tz: string;
  label: string;
} {
  const viewerTz = useViewerTimezone();
  return projectLocalTimeRange(dayOfWeek, startTimeUtc, endTimeUtc, viewerTz, refDate);
}

/** Read the current viewer's IANA timezone from auth store (default ET). */
export function useViewerTimezone(): string {
  return useAuthStore((s) => s.user?.timezone) ?? 'America/New_York';
}
